/**
 * @file src/pages/invoice-creation-wizard/components/CustomerSection.jsx
 * @description Customer section with client search.
 *
 * Client search no longer sends companyId. Backend derives the selected company
 * from the selected-company cookie.
 */

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { ErrorMessage } from "components/ui/FormValidator";
import Icon from "components/AppIcon";
import { searchClients } from "api/client.api";

// ---------------------------------------------------------------------------
// Debounce hook
// ---------------------------------------------------------------------------

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

// ---------------------------------------------------------------------------
// CustomerSection
// ---------------------------------------------------------------------------

const CustomerSection = ({
  data,
  onChange,
  onNestedChange,
  errors,
  shouldShowError,
  onFieldChange,
  onFieldBlur,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const dropdownRef = useRef(null);
  const debouncedName = useDebounce(data.name, 400);

  // ── Fetch search results ──────────────────────────────────────────────────
  useEffect(() => {
    if (!debouncedName || debouncedName.length < 3) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);

    searchClients(debouncedName)
      .then((clients) => {
        setSearchResults(clients);
        setShowDropdown(clients.length > 0);
      })
      .catch(() => {
        setSearchResults([]);
        setShowDropdown(false);
      })
      .finally(() => setIsSearching(false));
  }, [debouncedName]);

  // ── Close dropdown on outside click ──────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Auto-populate all fields when a customer is selected ─────────────────
  const handleSelectClient = (client) => {
    const clientId = client.id || client._id;
    onChange("customer", "clientId", clientId || "");
    onChange("customer", "name", client.name || "");
    onChange("customer", "vatNumber", client.vatNumber || "");
    onChange("customer", "crNumber", client.crNumber || "");

    onNestedChange(
      "customer",
      "registrationNumber",
      "schemeId",
      client.registrationScheme || "CRN",
    );

    onNestedChange("customer", "contact", "phone", client.contact || "");
    onNestedChange("customer", "contact", "email", client.email || "");

    const addr = client.address || {};

    onNestedChange("customer", "address", "street", addr.streetAddress || "");
    onNestedChange(
      "customer",
      "address",
      "buildingNumber",
      addr.buildingNumber || "",
    );
    onNestedChange(
      "customer",
      "address",
      "plotIdentification",
      addr.plotIdentification || "",
    );
    onNestedChange("customer", "address", "city", addr.city || "");
    onNestedChange(
      "customer",
      "address",
      "citySubdivision",
      addr.citySubdivision || "",
    );
    onNestedChange(
      "customer",
      "address",
      "province",
      addr.provinceRegion || "",
    );
    onNestedChange("customer", "address", "postalCode", addr.postalCode || "");

    onFieldChange("customer.name", client.name || "");

    setShowDropdown(false);
    setSearchResults([]);
  };

  const handleInputChange = (field, value) => {
    if (field === "name") {
      onChange("customer", "clientId", "");
    }

    onChange("customer", field, value);
    onFieldChange(`customer.${field}`, value);
  };

  const handleNestedInputChange = (section, field, value) => {
    onNestedChange("customer", section, field, value);
    onFieldChange(`customer.${section}.${field}`, value);
  };

  const handleInputBlur = (field, value) => {
    onFieldBlur(`customer.${field}`, value);
  };

  const handleNestedInputBlur = (section, field, value) => {
    onFieldBlur(`customer.${section}.${field}`, value);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700"
    >
      <div className="p-6">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
              <Icon name="User" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground dark:text-white">
                Customer Information
              </h3>
              <p className="text-sm text-muted-foreground dark:text-gray-300">
                Search existing customers or enter new details
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>

        {/* Customer Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground dark:text-white mb-3">
            Customer Type
          </label>
          <div className="grid grid-cols-2 gap-3">
            {["individual", "company"].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => handleInputChange("type", type)}
                className={`flex items-center gap-3 p-4 rounded-lg border transition-all duration-200 ${
                  data.type === type
                    ? "border-primary bg-primary-50 dark:bg-primary-900/20 text-primary"
                    : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                }`}
              >
                <Icon
                  name={type === "individual" ? "User" : "Building"}
                  size={20}
                />
                <div className="text-left">
                  <div className="font-medium capitalize">{type}</div>
                  <div className="text-xs text-muted-foreground dark:text-gray-400">
                    {type === "individual"
                      ? "B2C Transaction"
                      : "B2B Transaction"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Customer name with search dropdown */}
              <div className="relative" ref={dropdownRef}>
                <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                  {data.type === "company" ? "Company Name" : "Customer Name"} *
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    value={data.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    onBlur={(e) => handleInputBlur("name", e.target.value)}
                    placeholder={
                      data.type === "company"
                        ? "Search or enter company name"
                        : "Search or enter customer name"
                    }
                    className={
                      shouldShowError("customer.name") ? "border-error" : ""
                    }
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>

                {data.name.length > 0 && data.name.length < 3 && (
                  <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                    Type at least 3 characters to search existing customers
                  </p>
                )}

                <AnimatePresence>
                  {showDropdown && searchResults.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden"
                    >
                      {searchResults.map((client) => (
                        <button
                          key={client._id || client.id}
                          type="button"
                          onClick={() => handleSelectClient(client)}
                          className="w-full text-left px-4 py-3 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0"
                        >
                          <div className="font-medium text-foreground dark:text-white text-sm">
                            {client.name}
                          </div>
                          <div className="text-xs text-muted-foreground dark:text-gray-400 mt-0.5 flex gap-3">
                            {client.vatNumber && (
                              <span>VAT: {client.vatNumber}</span>
                            )}
                            {client.address?.city && (
                              <span>{client.address.city}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                <ErrorMessage
                  error={errors("customer.name")}
                  show={shouldShowError("customer.name")}
                />
              </div>

              {data.type === "company" && (
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    VAT Number *
                  </label>
                  <Input
                    type="text"
                    value={data.vatNumber}
                    onChange={(e) =>
                      handleInputChange("vatNumber", e.target.value)
                    }
                    onBlur={(e) => handleInputBlur("vatNumber", e.target.value)}
                    placeholder="300000000000000"
                    className={
                      shouldShowError("customer.vatNumber")
                        ? "border-error"
                        : ""
                    }
                  />
                  <ErrorMessage
                    error={errors("customer.vatNumber")}
                    show={shouldShowError("customer.vatNumber")}
                  />
                </div>
              )}

              {data.type === "company" && (
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    CR Number
                  </label>
                  <Input
                    type="text"
                    value={data.crNumber}
                    onChange={(e) =>
                      handleInputChange("crNumber", e.target.value)
                    }
                    placeholder="Enter CR number"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                  Registration Scheme
                </label>
                <Input
                  type="text"
                  value={data.registrationNumber?.schemeId || ""}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "registrationNumber",
                      "schemeId",
                      e.target.value,
                    )
                  }
                  onBlur={(e) =>
                    handleNestedInputBlur(
                      "registrationNumber",
                      "id",
                      e.target.value,
                    )
                  }
                  placeholder="CRN"
                />
              </div>
            </div>

            {/* Address Information */}
            <div>
              <h4 className="text-md font-medium text-foreground dark:text-white mb-4">
                Address Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    label: "Street Address *",
                    field: "street",
                    placeholder: "Enter street address",
                  },
                  {
                    label: "Building Number *",
                    field: "buildingNumber",
                    placeholder: "Enter building number",
                  },
                  {
                    label: "Plot Identification",
                    field: "plotIdentification",
                    placeholder: "Enter plot ID",
                  },
                  { label: "City *", field: "city", placeholder: "Enter city" },
                  {
                    label: "City Subdivision",
                    field: "citySubdivision",
                    placeholder: "Enter city subdivision",
                  },
                  {
                    label: "Province/Region",
                    field: "province",
                    placeholder: "Enter province/region",
                  },
                  {
                    label: "Postal Code *",
                    field: "postalCode",
                    placeholder: "Enter postal code",
                  },
                ].map(({ label, field, placeholder }) => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                      {label}
                    </label>
                    <Input
                      type="text"
                      value={data.address[field] || ""}
                      onChange={(e) =>
                        handleNestedInputChange(
                          "address",
                          field,
                          e.target.value,
                        )
                      }
                      onBlur={(e) =>
                        handleNestedInputBlur("address", field, e.target.value)
                      }
                      placeholder={placeholder}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h4 className="text-md font-medium text-foreground dark:text-white mb-4">
                Contact Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={data.contact.email}
                    onChange={(e) =>
                      handleNestedInputChange(
                        "contact",
                        "email",
                        e.target.value,
                      )
                    }
                    onBlur={(e) =>
                      handleNestedInputBlur("contact", "email", e.target.value)
                    }
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    value={data.contact.phone}
                    onChange={(e) =>
                      handleNestedInputChange(
                        "contact",
                        "phone",
                        e.target.value,
                      )
                    }
                    onBlur={(e) =>
                      handleNestedInputBlur("contact", "phone", e.target.value)
                    }
                    placeholder="+966 50 000 0000"
                  />
                </div>
              </div>
            </div>

            {/* ZATCA Compliance Notice */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon
                  name="Info"
                  size={20}
                  className="text-blue-600 dark:text-blue-400 mt-0.5"
                />
                <div>
                  <h5 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                    ZATCA Compliance
                  </h5>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {data.type === "company"
                      ? "For B2B transactions, VAT number is required for ZATCA compliance."
                      : "For B2C transactions, customer details are optional but recommended for record keeping."}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default CustomerSection;
